/* @ngInject */

export default (
  $q,
  $filter,
  $window,
  $rootScope,
  $timeout,
  logger,
  uiService,
  jobService,
  jobsService,
  JobProgress,
  JOB
) => {
  const order = {
    direction: false,
    property: 'dateDue'
  };

  /**
   * Get ordering direction.
   *
   * @returns {Boolean} - Is ordering direction ascending.
   */
  const orderGetDirection = () => order.direction;

  /**
   * Get ordering property.
   *
   * @returns {String} - Property to order by.
   */
  const orderGetProperty = () => order.property;

  /**
   * Sort jobs.
   *
   * @param {Array} jobs - Jobs array to sort.
   * @param {Boolean} [reverse] - Do not sort just reverse existing sort order.
   */
  const jobsSort = (jobs, reverse) => {
    if (reverse === true) {
      jobs.reverse();
      return;
    }
    switch (orderGetProperty()) {
      case 'name':
        jobs.sort((a, b) => {
          if (a.getName().toLowerCase() < b.getName().toLowerCase()) {
            return -1;
          }
          if (a.getName().toLowerCase() > b.getName().toLowerCase()) {
            return 1;
          }
          return 0;
        });
        break;
      case 'value':
        jobs.sort((a, b) => {
          if (a.getValue(100) < b.getValue(100)) {
            return -1;
          }
          if (a.getValue(100) > b.getValue(100)) {
            return 1;
          }
          return 0;
        });
        break;
      case 'dateEntry':
        jobs.sort((a, b) => {
          if (a.timeStamp < b.timeStamp) {
            return -1;
          }
          if (a.timeStamp > b.timeStamp) {
            return 1;
          }
          return 0;
        });
        break;
      case 'dateDue':
        jobs.sort((a, b) => {
          if (a.getDateSort() > b.getDateSort()) {
            return -1;
          }
          if (a.getDateSort() < b.getDateSort()) {
            return 1;
          }
          return 0;
        });
        break;
      default:
        break;
    }
    if (order.direction === false) {
      jobs.reverse();
    }
  };

  /**
   * Initialize progress view.
   * Append appropriate jobs to their progress, sort jobs in each stage.
   *
   * @param {Object} jobsProgress - Jobs progress.
   * @param {Array} jobs - Jobs.
   */
  const progressViewInitialize = (jobsProgress, jobs) => {
    const deferred = $q.defer();
    if (!jobsProgress || !jobsProgress.length || !jobs) {
      logger.log(
        'jobsProgressViewService',
        'progressViewInitialize',
        'Waiting for jobsProgress and jobs data'
      );
      deferred.reject();
    } else {
      logger.log(
        'jobsProgressViewService',
        'progressViewInitialize',
        'Processing jobs for current jobsProgress'
      );
      const len = jobsProgress.length;
      for (let i = 0; i < len; i += 1) {
        const filteredJobs = _.filter(
          jobs,
          (job) => job.jobProgress.name === jobsProgress[i].name
        );
        // eslint-disable-next-line no-param-reassign
        jobsProgress[i].jobs = filteredJobs;
        jobsSort(jobsProgress[i].jobs);
      }
      logger.log(
        'jobsProgressViewService',
        'progressViewInitialize',
        'Processed jobs for current jobsProgress'
      );
      deferred.resolve();
    }

    return deferred.promise;
  };

  /**
   * Retrieve the height for the board.
   *
   * @param {Object} jobsProgress - JobsProgress to get progress and jobs from.
   * @param {Number} [jobHeight] - Height for the single job tile.
   *
   * @returns {Object} - CSS Object with height in pixels.
   */
  const getProgressViewHeight = (jobsProgress, jobHeight) => {
    const windowHeight = angular.element($window).height();
    let jobsNumber = 0;
    const height = Number.isNaN(parseFloat(jobHeight)) ? JOB.UI.SINGLE_JOB_HEIGHT : jobHeight;
    jobsNumber = _.maxBy(jobsProgress, (progress) => progress.jobs.length).jobs.length;
    const progressMaxHeight = jobsNumber * height + JOB.UI.PROGRESS_VIEW_DIFFERENCE_HEIGHT;
    return windowHeight -
      JOB.UI.PROGRESS_VIEW_DIFFERENCE_HEIGHT -
      JOB.UI.PROGRESS_VIEW_HEADER_HEIGHT >
      progressMaxHeight
      ? { height: `${windowHeight - JOB.UI.PROGRESS_VIEW_HEADER_HEIGHT}px` }
      : { height: `${progressMaxHeight}px` };
  };

  /**
   * Set sorting direction and perform sorting.
   *
   * @param {Object} jobsProgress - JobsProgress object with progress and jobs to sort.
   * @param {Boolean} ascending - Set sorting direction.
   */
  const orderSetDirection = (jobsProgress, ascending) => {
    if (order.direction !== ascending) {
      order.direction = ascending;
      const len = jobsProgress.length;
      for (let i = 0; i < len; i += 1) {
        jobsSort(jobsProgress[i].jobs, true);
      }
    }
  };

  /**
   * Set sorting order and perform sorting.
   *
   * @param {Object} jobsProgress - JobsProgress object with progress and jobs to sort.
   * @param {String} property - Set sorting order by this property.
   */
  const orderSetProperty = (jobsProgress, property) => {
    if (order.property !== property) {
      order.property = property;
      const len = jobsProgress.length;
      for (let i = 0; i < len; i += 1) {
        jobsSort(jobsProgress[i].jobs);
      }
    }
  };

  /**
   * Move Job on board.
   *
   * @param {Object} ui - UI Sortable Model.
   * @param {Object} jobsProgress - JobsProgress.
   */
  const jobMove = (ui, jobsProgress) => {
    const deferred = $q.defer();
    logger.log('jobsProgressViewService', 'jobMove', 'Moving job on Board');
    if (
      !ui ||
      !ui.item ||
      !ui.item.sortable ||
      ui.item.sortable.isCanceled() ||
      ui.item.sortable.dropindex === undefined ||
      !jobsProgress ||
      !jobsProgress.length
    ) {
      deferred.reject();
    } else {
      const job = ui.item.sortable.model;
      const jobIndexNew = ui.item.sortable.dropindex;
      let progress = null;
      const len = jobsProgress.length;

      for (let i = 0; i < len; i += 1) {
        if (
          jobsProgress[i] &&
          jobsProgress[i].jobs &&
          jobsProgress[i].jobs.length >= jobIndexNew &&
          jobsProgress[i].jobs[jobIndexNew] &&
          jobsProgress[i].jobs[jobIndexNew].id === job.id
        ) {
          progress = jobsProgress[i];
          jobsSort(progress.jobs);
          break;
        }
      }

      if (progress.name === 'done' && !job.canBeCompleted()) {
        deferred.resolve(false);
        job.hasMoved = false;
      } else {
        job.hasMoved = true;
        job.jobProgress = _.find(JobProgress.available(), (p) => p.name === progress.name);
        job.isCompleted = progress.name === 'done';
        jobsService.clearCache();
        jobService.updateJob({
          id: job.id,
          jobProgress: job.jobProgress,
          isCompleted: job.isCompleted
        });
        $timeout(() => {
          if (progress.name === 'done') {
            progress.jobs.splice(jobIndexNew, 1);
          }
        }, 5000);
        deferred.resolve(true);
      }
    }

    return deferred.promise;
  };

  return {
    progressViewInitialize,
    getProgressViewHeight,
    orderGetDirection,
    orderGetProperty,
    orderSetDirection,
    orderSetProperty,
    jobMove,
    jobsSort
  };
};
